<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { initRemoteApps } from "@/config/remoteApps";
import {
  mediaCenterEvents,
  remoteAppEmitterEvents,
} from "@/helpers/mediaCenter.type";
import { UIContentWrap } from "@gohighlevel/ghl-ui";
import Postmate from "postmate";
import { onBeforeUnmount, ref } from "vue";
import { i18n } from "./plugin/vue-i18n";
import { useAppStore } from "./store/app";
import { GlobalSettingsEvents } from "./util/constants";
import { setupPendo } from "./util/pendoConfig";
import { setSentryMeta } from "./util/setupSentry";

initRemoteApps();
const appStore = useAppStore();
const emitter = useEmitter();

function onRefreshToken() {
  if (appStore.appHandshake) {
    appStore.appHandshake.then((parent: any) => parent.emit("refreshToken"));
  }
}

function onMediaLibraryOpen(settings: object) {
  if (appStore.appHandshake) {
    appStore.appHandshake.then((parent: any) =>
      parent.emit(mediaCenterEvents.MEDIA_CENTER_OPEN, settings)
    );
  }
}

emitter.on(remoteAppEmitterEvents.REFRESH_TOKEN, onRefreshToken);
emitter.on(mediaCenterEvents.MEDIA_CENTER_OPEN, onMediaLibraryOpen);

onBeforeUnmount(() => {
  emitter.off(remoteAppEmitterEvents.REFRESH_TOKEN, onRefreshToken);
  emitter.off(mediaCenterEvents.MEDIA_CENTER_OPEN, onMediaLibraryOpen);
});

if (process.env.NODE_ENV === "development") {
  Postmate.debug = true;
}

const locale = ref("en-US");
const handshake = new Postmate.Model({
  updatedToken: (token: string) => appStore.refreshToken(token),
  customFieldAdd: (customAdded: boolean) => appStore.getCustomFields(),
  updateResultTemplate: (template: object) =>
    appStore.updateResultTemplate(template),
  previewUrl: (previewData: string) => appStore.setPreviewUrl(previewData),
  sendIntegrateInfo: (integrate: object) =>
    appStore.setIntegrateInfo(integrate),
  back: () => appStore.back(),
  [GlobalSettingsEvents.GLOBAL_SETTINGS_CLOSED]: () =>
    appStore.loadBrandBoardCustomColors(),
  [mediaCenterEvents.MEDIA_CENTER_SELECTED]: (file: any) => {
    const callback = appStore.getAndClearPendingMediaCenterCallback();
    callback?.(file);
    emitter.emit(mediaCenterEvents.MEDIA_CENTER_SELECTED, file);
  },
});

handshake.then(async ({ model }: any) => {
  appStore.initialize(model);
  // Resolve multi-object creation from the location's labs features before the
  // builder becomes interactive (drop/validation logic reads this flag).
  await appStore.loadMultiObjectFeatureFlag();
  await appStore.getPaymentIntegrationDetails();
  appStore.setAppHandshake(handshake);
  appStore.loadBrandBoardCustomColors();
  appStore.loadBrandDefaultColors();
  appStore.loadDefaultColors();
  if (model.quizId) {
    await appStore.getQuiz();
  } else {
    await appStore.getFormOrSurvey();
  }
  appStore.getIntegrateInfo();
  appStore.getCustomFields();
  appStore.getCustomFieldFolders();
  appStore.getCustomObjects();
  await appStore.getPipelines();
  await appStore.getCustomValues();
  appStore.getPreviewUrl();
  appStore.getAllFonts();
  await appStore.getThemeList();
  if (model.formId || model.surveyId) {
    await appStore.getProductStoredInForms();
    await appStore.getProductList();
  }

  i18n.global.locale.value = model.locale ?? "en-US";
  locale.value = (model.locale || "en").replace("-", "_");
  localStorage.setItem("locale", model.locale);
  setSentryMeta({
    userId: appStore.userId,
    companyId: appStore.company.id,
    locationId: appStore.locationId,
    email: appStore.email,
    locale: model.locale,
  });
  setupPendo(model);
});
</script>

<template>
  <UIContentWrap :full-screen="true" :locale="(locale as any)">
    <router-view></router-view>
  </UIContentWrap>
</template>
